import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { Breadcrumb, Button, Descriptions, Dropdown, Layout as LayoutAnt, Menu, MenuProps, Modal, PageHeader, Popover, Space, Spin, Tag } from 'antd';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, NavLink } from 'react-router-dom';
import { Dispatch } from 'redux';
import { administratorsListAction } from '~Administrators/actions';
import {
    administratorsSelector,
    currentAdministratorSelector,
} from '~Administrators/selectors';
import IAdministrator from '~Api/Administrator/IAdministrator';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import RoleEnum from '~Api/Administrator/RoleEnum';
import CloseReasonEnum from '~Api/Application/CloseReasonEnum';
import ExtensionTypeEnum from '~Api/Application/ExtensionTypeEnum';
import IApplication from '~Api/Application/IApplication';
import IApplicationDisbursement from '~Api/Application/IApplicationDisbursement';
import IBorrower from '~Api/Application/IBorrower';
import InterestMethodEnum from '~Api/Application/InterestMethodEnum';
import WorkflowStatusEnum from '~Api/Application/WorkflowStatusEnum';
import IReferralPartner from '~Api/ReferralPartner/IReferralPartner';
import {
    applicationConditionalApprovalAction,
    applicationConditionsListAction,
    applicationDisbursementsListAction,
    applicationFormalApprovalAction,
    applicationFormalsAddAction,
    applicationLegalDocumentsAction,
    applicationNewAction,
    applicationReopenAction,
    applicationSettlementAction,
    applicationUnderwritingAction,
} from '~Applications/actions';
import {
    applicationDisbursementsSelector,
    applicationEntityConditionsSelector,
    applicationSelector,
    applicationWorkflowStatusMoveInProgressSelector,
} from '~Applications/selectors';
import SettlementDate from '~Applications/SettlementDate';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import BdmSelector from '~Deals/BdmSelector';
import BrokerSelector from '~Deals/BrokerSelector';
import FollowUpTime from '~Deals/FollowUpTime';
import LoanProcessorSelector from '~Deals/LoanProcessorSelector';
import { IGlobalState } from '~reducer';
import { referralPartnersListAction } from '~ReferralPartners/actions';
import { referralPartnersSelector } from '~ReferralPartners/selectors';
import { IDictionary } from '~utilities/IDictionary';
import './application.less';
import CloseModal from './CloseModal';
import SettledModal from './SettledModal';
import ICondition from '~Api/Application/ICondition';
import AssigneeSelector from './AssigneeSelector';
import IAggregator from '~Api/Aggregator/IAggregator';
import { aggregatorsSelector } from '~Aggregators/selectors';
import { aggregatorsListAction } from '~Aggregators/actions';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import menuDivider from '~UI/menuDivider';
import { currencyFormatter } from '~utilities/formatters';
import { Helmet } from 'react-helmet';

enum SectionEnum {
    Borrowers = 'BORROWERS',
    ConditionalApprovals = 'CONDITIONAL_APPROVALS',
    CustomConditions = 'CUSTOM_CONDITIONS',
    Disbursements = 'DISBURSEMENTS',
    Documents = 'DOCUMENTS',
    Edit = 'EDIT',
    Fees = 'FEES',
    FormalApprovals = 'FORMAL_APPROVALS',
    History = 'HISTORY',
    Notes = 'NOTES',
    Overview = 'OVERVIEW',
    Properties = 'PROPERTIES',
    PropertyValuations = 'PROPERTY_VALUATIONS',
    RequiredInformation = 'REQUIRED_INFORMATION',
    SolicitorInstructions = 'SOLICITOR_INSTRUCTIONS',
    Underwriting = 'UNDERWRITING',
    Warehouses = 'WAREHOUSES',
}

enum WorkflowStepEnum {
    ConditionalApproval = 'CONDITIONAL_APPROVAL',
    Draft = 'DRAFT',
    FormalApproval = 'FORMAL_APPROVAL',
    IssueConditionalApproval = 'ISSUE_CONDITIONAL_APPROVAL',
    IssueFormalApproval = 'ISSUE_FORMAL_APPROVAL',
    LegalDocuments = 'LEGAL_DOCUMENTS',
    New = 'NEW',
    Settled = 'SETTLED',
    Settlement = 'SETTLEMENT',
    Underwriting = 'UNDERWRITING',
}

const closeReasonLabels: IDictionary<string> = {
    [CloseReasonEnum.Cancelled]: 'Cancelled',
    [CloseReasonEnum.Cold]: 'Cold/Stale',
    [CloseReasonEnum.Credit]: 'Credit Issue',
    [CloseReasonEnum.LostToOther]: 'Lost to Other',
    [CloseReasonEnum.Settled]: 'Settled',
    [CloseReasonEnum.Valuation]: 'Valuation Issue',
};

const statusNames: IDictionary<string> = {
    [WorkflowStatusEnum.Draft]: 'Draft',
    [WorkflowStatusEnum.New]: 'New',
    [WorkflowStatusEnum.ConditionalApproval]: 'Conditional Approval',
    [WorkflowStatusEnum.Underwriting]: 'Underwriting',
    [WorkflowStatusEnum.FormalApproval]: 'Formal Approval',
    [WorkflowStatusEnum.LegalDocuments]: 'Legal Documents',
    [WorkflowStatusEnum.Settlement]: 'Settlement',
    [WorkflowStatusEnum.Warehoused]: 'Settled',
};

const buttonWorkflowStepAllowedStatusesMap: IDictionary<WorkflowStatusEnum[]> = {
    [WorkflowStepEnum.ConditionalApproval]: [],
    [WorkflowStepEnum.FormalApproval]: [WorkflowStatusEnum.Underwriting],
    [WorkflowStepEnum.IssueConditionalApproval]: [WorkflowStatusEnum.New],
    [WorkflowStepEnum.IssueFormalApproval]: [WorkflowStatusEnum.FormalApproval],
    [WorkflowStepEnum.LegalDocuments]: [],
    [WorkflowStepEnum.New]: [WorkflowStatusEnum.Draft],
    [WorkflowStepEnum.Settled]: [WorkflowStatusEnum.Settlement],
    [WorkflowStepEnum.Settlement]: [WorkflowStatusEnum.LegalDocuments],
    [WorkflowStepEnum.Underwriting]: [WorkflowStatusEnum.ConditionalApproval],
};

const menuItemWorkflowStepAllowedStatusesMap: IDictionary<WorkflowStatusEnum[]> = {
    [WorkflowStepEnum.ConditionalApproval]: [
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Underwriting,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.Draft]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Underwriting,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.FormalApproval]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.LegalDocuments]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Underwriting,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.New]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Underwriting,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.Settled]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Underwriting,
    ],
    [WorkflowStepEnum.Settlement]: [
        WorkflowStatusEnum.ConditionalApproval,
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Underwriting,
        WorkflowStatusEnum.Warehoused,
    ],
    [WorkflowStepEnum.Underwriting]: [
        WorkflowStatusEnum.Draft,
        WorkflowStatusEnum.FormalApproval,
        WorkflowStatusEnum.LegalDocuments,
        WorkflowStatusEnum.New,
        WorkflowStatusEnum.Settlement,
        WorkflowStatusEnum.Warehoused,
    ],
};

export default function Layout(props: {
    applicationUuid: string;
    breadcrumb?: JSX.Element;
    children: JSX.Element | JSX.Element[];
    section: string;
}): ReactElement {
    const { applicationUuid, breadcrumb, children, section } = props;

    const [ isCloseModalOpen, setIsCloseModalOpen ] = useState<boolean>(false);
    const [ isSettledModalOpen, setIsSettledModalOpen ] = useState<boolean>(false);

    const administrators: IDictionary<IAdministrator> = useSelector(administratorsSelector);
    const aggregators: IDictionary<IAggregator> = useSelector(aggregatorsSelector);
    const application: IApplication = useSelector((state: IGlobalState) => applicationSelector(state, applicationUuid));
    const applicationEntityConditions: IDictionary<IDictionary<ICondition>> = useSelector((state: IGlobalState) => applicationEntityConditionsSelector(state, applicationUuid));
    const currentAdministrator: IAdministrator = useSelector(currentAdministratorSelector);
    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const disbursements: IDictionary<IApplicationDisbursement> = useSelector((state: IGlobalState) => applicationDisbursementsSelector(state, applicationUuid));
    const partners: IDictionary<IReferralPartner> = useSelector(referralPartnersSelector);
    const isWorkflowStatusMoveInProgress: boolean = useSelector(applicationWorkflowStatusMoveInProgressSelector);
    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!administrators) {
            dispatch(administratorsListAction());
        }
    }, [
        administrators,
        dispatch,
    ]);

    useEffect(() => {
        if (!aggregators) {
            dispatch(aggregatorsListAction());
        }
    }, [
        aggregators,
        dispatch,
    ]);

    useEffect(() => {
        if (!applicationEntityConditions) {
            dispatch(applicationConditionsListAction(applicationUuid));
        }
    }, [
        applicationEntityConditions,
        applicationUuid,
        dispatch,
    ]);

    useEffect(() => {
        if (!disbursements) {
            dispatch(applicationDisbursementsListAction(applicationUuid));
        }
    }, [
        applicationUuid,
        disbursements,
        dispatch,
    ]);

    useEffect(() => {
        if (!partners) {
            dispatch(referralPartnersListAction());
        }
    }, [
        dispatch,
        partners,
    ]);

    const shouldDisplaySection: (section: SectionEnum) => boolean = useCallback((sectionParam: SectionEnum) => {
        const isGracePeriod: boolean = ExtensionTypeEnum.GracePeriod === application.extensionType;
        const isRenewal: boolean = ExtensionTypeEnum.Renewal === application.extensionType;
        const isBdm: boolean = [
            RoleEnum.BusinessDevelopmentManager,
            RoleEnum.InternalBusinessDevelopmentManager,
            RoleEnum.SeniorBusinessDevelopmentManager,
        ].includes(currentAdministrator.role);

        switch (sectionParam) {
            case SectionEnum.Borrowers:
                return !isBdm;
            case SectionEnum.ConditionalApprovals:
                return !application.legacy && !isGracePeriod && !isRenewal;
            case SectionEnum.CustomConditions:
                return !application.legacy && !isBdm && !isGracePeriod;
            case SectionEnum.Disbursements:
                return !isBdm && !isGracePeriod;
            case SectionEnum.Documents:
                return true;
            case SectionEnum.Edit:
                return !isBdm;
            case SectionEnum.Fees:
                return !application.legacy && !isBdm && !isGracePeriod;
            case SectionEnum.FormalApprovals:
                return !application.legacy && !isBdm && !isGracePeriod && !isRenewal;
            case SectionEnum.History:
                return true;
            case SectionEnum.Notes:
                return true;
            case SectionEnum.Overview:
                return true;
            case SectionEnum.Properties:
                return true;
            case SectionEnum.PropertyValuations:
                return !isBdm && !isGracePeriod;
            case SectionEnum.SolicitorInstructions:
                return !application.legacy && !isBdm && !isGracePeriod && !isRenewal;
            case SectionEnum.Underwriting:
                return !application.legacy && !isBdm && !isGracePeriod;
            case SectionEnum.RequiredInformation:
                return !application.legacy && !isGracePeriod;
            case SectionEnum.Warehouses:
                return !application.legacy && currentUser.permissions.includes(PermissionsEnum.ApplicationWarehouses);
        }

        return false;
    }, [
        application,
        currentAdministrator,
        currentUser,
    ]);

    const canMoveToWorkflowStep: (workflowStep: WorkflowStepEnum) => boolean = useCallback((workflowStep: WorkflowStepEnum) => {
        const isGracePeriod: boolean = ExtensionTypeEnum.GracePeriod === application.extensionType;
        const isRenewal: boolean = ExtensionTypeEnum.Renewal === application.extensionType;

        switch (workflowStep) {
            case WorkflowStepEnum.ConditionalApproval:
                return !isGracePeriod;
            case WorkflowStepEnum.Draft:
                return !isGracePeriod && !isRenewal;
            case WorkflowStepEnum.FormalApproval:
                return !isGracePeriod;
            case WorkflowStepEnum.IssueConditionalApproval:
                return !isGracePeriod && !isRenewal;
            case WorkflowStepEnum.IssueFormalApproval:
                return !isGracePeriod && !isRenewal && (currentUser.permissions.includes(PermissionsEnum.ApplicationFormalApprovalAdd));
            case WorkflowStepEnum.LegalDocuments:
                return !isGracePeriod && (currentUser.permissions.includes(PermissionsEnum.ApplicationFormalApprovalAdd) || [WorkflowStatusEnum.Settlement, WorkflowStatusEnum.Warehoused].includes(application.workflowStatus));
            case WorkflowStepEnum.New:
                return !isGracePeriod;
            case WorkflowStepEnum.Settled:
                return (
                    application.interestMethod !== InterestMethodEnum.InterestMethod360
                    && currentUser.permissions.includes(PermissionsEnum.ApplicationSettle)
                );
            case WorkflowStepEnum.Settlement:
                return !isGracePeriod && (currentUser.permissions.includes(PermissionsEnum.ApplicationFormalApprovalAdd) || [WorkflowStatusEnum.LegalDocuments, WorkflowStatusEnum.Warehoused].includes(application.workflowStatus));
            case WorkflowStepEnum.Underwriting:
                return !isGracePeriod;
        }

        return false;
    }, [
        application,
        currentUser,
    ]);

    const shouldDisplayWorkflowMenuItem: (workflowStep: WorkflowStepEnum) => boolean = useCallback((workflowStep: WorkflowStepEnum) => {
        if (!canMoveToWorkflowStep(workflowStep)) {
            return false;
        }

        return menuItemWorkflowStepAllowedStatusesMap[workflowStep].includes(application.workflowStatus);
    }, [
        application,
        canMoveToWorkflowStep,
    ]);

    const shouldDisplayWorkflowButton: (workflowStep: WorkflowStepEnum) => boolean = useCallback((workflowStep: WorkflowStepEnum) => {
        if (application.closeReason || application.legacy) {
            return false;
        }

        if (!canMoveToWorkflowStep(workflowStep)) {
            return false;
        }

        return buttonWorkflowStepAllowedStatusesMap[workflowStep].includes(application.workflowStatus);
    }, [
        application,
        canMoveToWorkflowStep,
    ]);

    const onClickClose: () => void = useCallback(() => {
        setIsCloseModalOpen(true);
    }, []);

    const onCloseCancel: () => void = useCallback(() => {
        setIsCloseModalOpen(false);
    }, []);

    const onClickUnderwriting: () => void = useCallback(() => {
        dispatch(applicationUnderwritingAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickFormalApproval: () => void = useCallback(() => {
        dispatch(applicationFormalApprovalAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickIssueFormalApproval: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to issue a formal approval for this application?',
            okText: 'Issue Formal Approval',
            onOk: () => {
                dispatch(applicationFormalsAddAction(applicationUuid));
            },
            title: 'Issue Formal Approval',
        });
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickLegalDocuments: () => void = useCallback(() => {
        dispatch(applicationLegalDocumentsAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickSettlement: () => void = useCallback(() => {
        dispatch(applicationSettlementAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const onClickSettled: () => void = useCallback(() => {
        setIsSettledModalOpen(true);
    }, []);

    const onSettledCancel: () => void = useCallback(() => {
        setIsSettledModalOpen(false);
    }, []);

    const onClickReopen: () => void = useCallback(() => {
        Modal.confirm({
            content: 'Are you sure you want to reopen this application?',
            okText: 'Reopen',
            onOk: () => {
                dispatch(applicationReopenAction(applicationUuid));
            },
            title: 'Reopen Application',
        });
    }, [
        applicationUuid,
        dispatch,
    ]);

    const conditionalApproval: () => void = useCallback(() => {
        dispatch(applicationConditionalApprovalAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    const moveNew: () => void = useCallback(() => {
        dispatch(applicationNewAction(applicationUuid));
    }, [
        applicationUuid,
        dispatch,
    ]);

    if (!application || !currentAdministrator || !partners) {
        return (
            <LayoutAnt className='application'>
                <Spin />
            </LayoutAnt>
        );
    }

    const isGracePeriod: boolean = ExtensionTypeEnum.GracePeriod === application.extensionType;
    const isRenewal: boolean = ExtensionTypeEnum.Renewal === application.extensionType;

    const primaryBorrower: IBorrower = _.find(application.borrowers, { uuid: application.primaryBorrowerUuid }) as IBorrower;

    const breadcrumbBlock: JSX.Element = breadcrumb ? (
        <>
            <Breadcrumb.Item><Link to={`/applications/${application.uuid}`}>{application.code}</Link></Breadcrumb.Item>
            {breadcrumb}
        </>
    ) : (
        <Breadcrumb.Item>{application.code}</Breadcrumb.Item>
    );

    const workflowMenu: MenuProps = {
        items: [
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.New) ? [
                {
                    key: 'new',
                    label: 'Move to New',
                    onClick: moveNew,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.ConditionalApproval) ? [
                {
                    key: 'conditional-approval',
                    label: 'Move to Conditional Approval',
                    onClick: conditionalApproval,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.Underwriting) ? [
                {
                    key: 'underwriting',
                    label: 'Move to Underwriting',
                    onClick: onClickUnderwriting,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.FormalApproval) ? [
                {
                    key: 'formal-approval',
                    label: 'Move to Formal Approval',
                    onClick: onClickFormalApproval,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.LegalDocuments) ? [
                {
                    key: 'legal-documents',
                    label: 'Move to Legal Documents',
                    onClick: onClickLegalDocuments,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.Settlement) ? [
                {
                    key: 'settlement',
                    label: 'Move to Settlement',
                    onClick: onClickSettlement,
                },
            ] : []),
            ...(shouldDisplayWorkflowMenuItem(WorkflowStepEnum.Settled) ? [
                {
                    key: 'settled',
                    label: 'Move to Settled',
                    onClick: onClickSettled,
                },
            ] : []),
        ],
    };

    const extra: JSX.Element = ![
        RoleEnum.BusinessDevelopmentManager,
        RoleEnum.InternalBusinessDevelopmentManager,
        RoleEnum.SeniorBusinessDevelopmentManager,
    ].includes(currentAdministrator.role) && (
        <Space>
            <Tag className='workflow-status'>{statusNames[application.workflowStatus]}</Tag>
            {!isGracePeriod && !application.legacy && !application.closeReason && <Button danger={true} onClick={onClickClose}>Close</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.New) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={moveNew}>Move to New</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.IssueConditionalApproval) && <Link to={`/applications/${application.uuid}/conditional-approvals/add`}><Button type='primary' loading={isWorkflowStatusMoveInProgress}>Issue Conditional Approval</Button></Link>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.Underwriting) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={onClickUnderwriting}>Move to Underwriting</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.FormalApproval) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={onClickFormalApproval}>Move to Formal Approval</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.IssueFormalApproval) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={onClickIssueFormalApproval}>Issue Formal Approval</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.Settlement) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={onClickSettlement}>Move to Settlement</Button>}
            {shouldDisplayWorkflowButton(WorkflowStepEnum.Settled) && <Button type='primary' loading={isWorkflowStatusMoveInProgress} onClick={onClickSettled}>Move to Settled</Button>}
            {!isGracePeriod && !application.legacy && !application.closeReason && <Dropdown.Button className='workflow-dropdown-button' menu={workflowMenu} />}
            {!isGracePeriod && !isRenewal && !application.legacy && application.closeReason && application.closeReason !== CloseReasonEnum.Cancelled && application.workflowStatus !== WorkflowStatusEnum.Warehoused && <Button danger={true} onClick={onClickReopen}>Reopen</Button>}
        </Space>
    );

    const bdm: IAdministrator = application.deal.bdmUuid && administrators[application.deal.bdmUuid];

    const contactContentBlock: JSX.Element = bdm ? (
        <React.Fragment>
            <p>Email: <a href={`mailto:${bdm.email}`}>{bdm.email || '-'}</a></p>
        </React.Fragment>
    ) : (application.deal.broker ? (
        <React.Fragment>
            <p>Phone: <a href={`tel:${application.deal.broker.phone}`}>{application.deal.broker.phone || '-'}</a></p>
            <p>Email: <a href={`mailto:${application.deal.broker.email}`}>{application.deal.broker.email || '-'}</a></p>
        </React.Fragment>
    ) : (
        <React.Fragment>
            <p>Phone: <a href={`tel:${primaryBorrower && primaryBorrower.dealBorrower.phone}`}>{primaryBorrower ? primaryBorrower.dealBorrower.phone || '-' : '-'}</a></p>
            <p>Email: <a href={`mailto:${primaryBorrower && primaryBorrower.dealBorrower.email}`}>{primaryBorrower ? primaryBorrower.dealBorrower.email || '-' : '-'}</a></p>
        </React.Fragment>
    ));

    const contactBlock: JSX.Element | string = bdm ? (
        <Popover content={contactContentBlock} title={`${bdm.name} (BDM)`}>
            <a>{bdm.name} (BDM)</a>
        </Popover>
    ) : (application.deal.broker ? (
        <Popover content={contactContentBlock} title={`${application.deal.broker.firstName} ${application.deal.broker.lastName} (Broker)`}>
            <a>{application.deal.broker.firstName} {application.deal.broker.lastName} (Broker)</a>
        </Popover>
    ) : (
        <Popover content={contactContentBlock} title={`${primaryBorrower ? primaryBorrower.dealBorrower.formattedName : '-'}`}>
            <a>{primaryBorrower ? primaryBorrower.dealBorrower.formattedName : '-'}</a>
        </Popover>
    ));

    const closeReasonBlock: JSX.Element = application.closeReason && (
        <Descriptions.Item label='Close Reason'>{application.closeReason ? closeReasonLabels[application.closeReason] : 'None'}</Descriptions.Item>
    );

    const tagsBlock: JSX.Element = application.isExtension && (isGracePeriod ? <Tag color='orange'>Grace Period</Tag> : <Tag color='blue'>Extension</Tag>);

    const menuItems: ItemType[] = [
        ...(shouldDisplaySection(SectionEnum.Overview) ? [
            {
                key: 'overview',
                label: <NavLink to={`/applications/${application.uuid}`}>Overview</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Edit) ? [
            {
                key: 'edit',
                label: <NavLink to={`/applications/${application.uuid}/edit`}>Edit</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Borrowers) ? [
            {
                key: 'borrowers',
                label: <NavLink to={`/applications/${application.uuid}/borrowers`}>Borrowers</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.ConditionalApprovals) ? [
            {
                key: 'conditional-approvals',
                label: <NavLink to={`/applications/${application.uuid}/conditional-approvals`}>Conditional Approvals</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.FormalApprovals) ? [
            {
                key: 'formal-approvals',
                label: <NavLink to={`/applications/${application.uuid}/formal-approvals`}>Formal Approvals</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Fees) ? [
            {
                key: 'fees',
                label: <NavLink to={`/applications/${application.uuid}/fees`}>Fees &amp; Outlays</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Underwriting) ? [
            {
                key: 'underwriting',
                label: <NavLink to={`/applications/${application.uuid}/underwriting`}>Underwriting</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.RequiredInformation) ? [
            {
                key: 'required-information',
                label: <NavLink to={`/applications/${application.uuid}/required-information`}>Required Information</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.PropertyValuations) ? [
            {
                key: 'property-valuations',
                label: <NavLink to={`/applications/${application.uuid}/property-valuations`}>Property Valuations</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.SolicitorInstructions) ? [
            {
                key: 'solicitor-instructions',
                label: <NavLink to={`/applications/${application.uuid}/solicitor-instructions`}>Solicitor Instructions</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Disbursements) ? [
            {
                key: 'disbursements',
                label: <NavLink to={`/applications/${application.uuid}/disbursements`}>Disbursements</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Warehouses) ? [
            {
                key: 'warehouses',
                label: <NavLink to={`/applications/${application.uuid}/warehouses`}>Warehouses</NavLink>,
            },
        ] : []),
        menuDivider,
        ...(shouldDisplaySection(SectionEnum.Properties) ? [
            {
                key: 'properties',
                label: <NavLink to={`/applications/${application.uuid}/properties`}>Properties</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Documents) ? [
            {
                key: 'documents',
                label: <NavLink to={`/applications/${application.uuid}/documents`}>Documents</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.Notes) ? [
            {
                key: 'notes',
                label: <NavLink to={`/applications/${application.uuid}/notes`}>Notes</NavLink>,
            },
        ] : []),
        ...(shouldDisplaySection(SectionEnum.History) ? [
            {
                key: 'history',
                label: <NavLink to={`/applications/${application.uuid}/history`}>History</NavLink>,
            },
        ] : []),
        menuDivider,
        {
            key: 'lead',
            label: <NavLink to={`/leads/${application.dealUuid}`}><ArrowLeftOutlined /> Lead</NavLink>,
        },
        ...(application.previousLoanUuid && currentUser.permissions.includes(PermissionsEnum.Loans) ? [
            {
                key: 'previous-loan',
                label: <NavLink to={`/loans/${application.previousLoanUuid}`}><ArrowLeftOutlined /> Previous Loan</NavLink>,
            },
        ] : []),
        ...(application.loanUuid ? [
            {
                key: 'loan',
                label: <NavLink to={`/loans/${application.loanUuid}`}><ArrowRightOutlined /> Loan</NavLink>,
            },
        ] : []),
    ];

    return (
        <LayoutAnt className='application'>
            <Helmet>
                <title>{application.code} - {application.formattedName}</title>
            </Helmet>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item><Link to='/applications'>Applications</Link></Breadcrumb.Item>
                {breadcrumbBlock}
            </Breadcrumb>
            <PageHeader
                className='header'
                extra={extra}
                ghost={false}
                subTitle={application.code}
                title={application.formattedName}
                tags={tagsBlock}
            >
                <Descriptions size='small' column={4}>
                    <Descriptions.Item label='Loan Amount'>{currencyFormatter.format(application.loanAmount)}</Descriptions.Item>
                    <Descriptions.Item label='Term'>{application.termMonths} months {application.termMonthsMinimum && `(min. ${application.termMonthsMinimum} months)`}</Descriptions.Item>
                    <Descriptions.Item label='Interest Rate'>{application.interestRate}%</Descriptions.Item>
                    <Descriptions.Item className='settlement-date' label='Settlement Date'><SettlementDate application={application} /></Descriptions.Item>
                    <Descriptions.Item label='Loan Officer'>
                        <AssigneeSelector application={application} />
                    </Descriptions.Item>
                    <Descriptions.Item label='Loan Processors'>
                        <LoanProcessorSelector deal={application.deal} />
                    </Descriptions.Item>
                    <Descriptions.Item label='BDM'>
                        <BdmSelector deal={application.deal} />
                    </Descriptions.Item>
                    <Descriptions.Item label='Broker'>
                        <BrokerSelector deal={application.deal} />
                    </Descriptions.Item>
                    <Descriptions.Item label='Contact'>{contactBlock}</Descriptions.Item>
                    {closeReasonBlock}
                    <Descriptions.Item label='Follow Up'><FollowUpTime dealUuid={application.deal.uuid} /></Descriptions.Item>
                </Descriptions>
            </PageHeader>
            <LayoutAnt className='content-wrapper'>
                <LayoutAnt.Sider className='menu' theme='light' width={240}>
                    <Menu
                        defaultSelectedKeys={[section]}
                        items={menuItems}
                        mode='inline'
                    />
                </LayoutAnt.Sider>
                <LayoutAnt.Content className={`content ${section}`}>
                    {children}
                </LayoutAnt.Content>
            </LayoutAnt>
            <CloseModal
                application={application}
                isOpen={isCloseModalOpen}
                onCancel={onCloseCancel}
            />
            <SettledModal
                application={application}
                isOpen={isSettledModalOpen}
                onCancel={onSettledCancel}
            />
        </LayoutAnt>
    );
}
